/**
 * Clasificacion del caso dentro de la carpeta de investigacion. Vive en
 * `folder.case_classification` y es lo que despues alimenta el reporte
 * disciplinario: tipo de caso, que se comprobo, que tan grave es y con que
 * numero se sigue en el sistema de disciplina.
 *
 * El responsable asignado NO vive aqui: ya se guarda en `folder.lead_user_id`.
 */

// Catalogo del tipo de caso. Es global (no del tenant), igual que en los
// formularios de denuncia.
export const CASE_TYPE_CATALOGUE = "RC-100";
export const CASE_TYPE_IS_OWN = false;

/** ¿Se comprobaron los hechos? Lista cerrada, no viene de catalogo. */
export const FACTS_VERIFIED = {
  CORROBORATED: "corroborated",
  NOT_CORROBORATED: "not_corroborated",
  PERFORMANCE_ISSUE: "performance_issue",
  CREATED_IN_ERROR: "created_in_error",
};

export const FACTS_VERIFIED_VALUES = Object.values(FACTS_VERIFIED);

/**
 * Escala de sancion / severidad. Va de MAYOR a MENOR gravedad: el 1 es la
 * terminacion laboral y el 5 el simple recordatorio de politicas. No invertir
 * el orden ni ordenar por "severidad ascendente": asi lo maneja el banco.
 *
 * Las etiquetas viven en `caseClassification.severity_1..5` de las
 * traducciones.
 */
export const SEVERITY_LEVELS = [1, 2, 3, 4, 5];

/**
 * Clasificacion GRC. Son cuatro niveles jerarquicos que hoy se capturan a
 * mano: el catalogo todavia no existe. Cuando exista, esto se cambia por un
 * <InputCatalogue> —que ya encadena los niveles solo— y `grc` pasa a guardar
 * el path en vez de los cuatro textos.
 */
export const GRC_LEVELS = 4;

/** Una clasificacion: el path del catalogo y su etiqueta ya resuelta. */
export const emptyCaseType = () => ({ path: "", label: "" });

/**
 * Separador con el que se arma la cadena del catalogo ("Denuncia > Fraude
 * interno / Colusion"). Se guarda la cadena completa, pero en pantalla solo se
 * muestra el ultimo nivel: la rama de arriba es siempre la misma y solo estorba.
 */
export const CASE_TYPE_SEPARATOR = " > ";

/**
 * Etiqueta a mostrar de una clasificacion: solo el ultimo nivel.
 *   "Denuncia > Fraude interno / Colusion" -> "Fraude interno / Colusion"
 * Corta tambien lo ya guardado con la cadena completa, asi los expedientes
 * viejos se ven igual sin tener que volver a guardarlos.
 */
export const caseTypeLabel = (caseType) => {
  const label = cleanText(caseType?.label);
  if (!label) return cleanText(caseType?.path);
  return label.split(CASE_TYPE_SEPARATOR).pop().trim() || label;
};

export const emptyCaseClassification = () => ({
  // Un caso puede caer en varias clasificaciones de RC-100 (p.ej. acoso +
  // represalia), por eso es lista. Se guarda en `case_types`; `case_type` (en
  // singular) es el formato viejo, de cuando solo se permitia una.
  case_types: [],
  accusation: "",
  facts_verified: "",
  severity: null,
  grc: Array(GRC_LEVELS).fill(""),
  discipline_case_number: "",
});

const cleanText = (value) => (typeof value === "string" ? value : "");

/** Severidad valida (1-5) o null. */
export const parseSeverity = (value) => {
  const number = Number(value);
  return SEVERITY_LEVELS.includes(number) ? number : null;
};

/**
 * Clasificaciones de lo que venga del API, en lista.
 *
 * Lee `case_types`; si no está, cae al `case_type` suelto que guardaban los
 * expedientes anteriores a la multi-clasificación. Se descartan las entradas
 * sin path y las repetidas: dos veces la misma clasificación no dice nada.
 */
const normalizeCaseTypes = (classification) => {
  const list = Array.isArray(classification?.case_types)
    ? classification.case_types
    : classification?.case_type
    ? [classification.case_type]
    : [];

  const seen = new Set();
  return list.reduce((acc, item) => {
    const path = cleanText(item?.path);
    if (!path || seen.has(path)) return acc;
    seen.add(path);
    acc.push({ path, label: cleanText(item?.label) });
    return acc;
  }, []);
};

/** Acepta lo que venga del API y lo ordena. */
export const normalizeCaseClassification = (classification) => {
  const base = emptyCaseClassification();
  if (!classification || typeof classification !== "object") return base;

  const grc = Array.isArray(classification.grc) ? classification.grc : [];

  return {
    case_types: normalizeCaseTypes(classification),
    accusation: cleanText(classification.accusation),
    facts_verified: FACTS_VERIFIED_VALUES.includes(classification.facts_verified)
      ? classification.facts_verified
      : "",
    severity: parseSeverity(classification.severity),
    grc: Array.from({ length: GRC_LEVELS }, (_, i) => cleanText(grc[i])),
    discipline_case_number: cleanText(classification.discipline_case_number),
  };
};

export const isCaseClassificationEmpty = (classification) => {
  const c = normalizeCaseClassification(classification);
  return (
    c.case_types.length === 0 &&
    !c.accusation.trim() &&
    !c.facts_verified &&
    c.severity === null &&
    !c.grc.some((level) => level.trim()) &&
    !c.discipline_case_number.trim()
  );
};

/**
 * Lo que se manda al API. La severidad viaja como string porque el cifrado del
 * folder solo alcanza strings: un numero se guardaria en claro.
 */
export const serializeCaseClassification = (classification) => {
  const c = normalizeCaseClassification(classification);
  return {
    case_types: c.case_types.map(({ path, label }) => ({ path, label })),
    // La primera tambien se guarda en el formato viejo: hay expedientes y
    // pantallas que todavia leen `case_type` en singular.
    case_type: c.case_types[0] ?? emptyCaseType(),
    accusation: c.accusation.trim(),
    facts_verified: c.facts_verified,
    severity: c.severity === null ? "" : String(c.severity),
    grc: c.grc.map((level) => level.trim()),
    discipline_case_number: c.discipline_case_number.trim(),
  };
};
